import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  TrendingDown, 
  Clock, 
  CheckCircle, 
  DollarSign, 
  Activity,
  XCircle 
} from 'lucide-react';
import { CashOutflowKPIs as KPIData } from '@/hooks/finance/useCashOutflowReport';
import { formatCurrency } from '@/lib/currency';

interface CashOutflowKPIsProps {
  kpis: KPIData;
  isLoading: boolean;
  displayCurrency?: string;
}

export const CashOutflowKPIs: React.FC<CashOutflowKPIsProps> = ({ kpis, isLoading, displayCurrency = 'ETB' }) => {
  const fmtCurr = (amount: number) => formatCurrency(amount, displayCurrency);
  const kpiCards = [
    {
      title: 'Total Cash Outflow',
      value: fmtCurr(kpis.totalCashOutflow),
      icon: TrendingDown,
      description: `${kpis.totalTransactions} transactions`,
      color: 'text-red-500',
      bgColor: 'bg-red-500/10',
    },
    {
      title: 'Pending Outflow',
      value: fmtCurr(kpis.pendingOutflow),
      icon: Clock,
      description: 'Approved, awaiting payment',
      color: 'text-amber-500',
      bgColor: 'bg-amber-500/10',
    },
    {
      title: 'Avg Processing Time',
      value: `${kpis.avgProcessingDays} days`,
      icon: Activity,
      description: 'Submission to payment',
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10',
    },
    {
      title: 'Approval Rate',
      value: `${kpis.approvalRate}%`,
      icon: CheckCircle,
      description: `${kpis.rejectedCount} rejected`,
      color: 'text-green-500',
      bgColor: 'bg-green-500/10',
    },
    {
      title: 'Monthly Burn Rate',
      value: fmtCurr(kpis.monthlyBurnRate),
      icon: DollarSign,
      description: '3-month average',
      color: 'text-purple-500',
      bgColor: 'bg-purple-500/10',
    },
    ...(kpis.internalTransfersCount > 0 ? [{
      title: 'Transfers (excluded)',
      value: fmtCurr(kpis.internalTransfersExcluded),
      icon: XCircle,
      description: `${kpis.internalTransfersCount} inter-account transfers`,
      color: 'text-muted-foreground',
      bgColor: 'bg-muted/50',
    }] : []),
  ];

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {[...Array(5)].map((_, i) => (
          <Card key={i}>
            <CardContent className="pt-6">
              <Skeleton className="h-4 w-24 mb-2" />
              <Skeleton className="h-8 w-32 mb-1" />
              <Skeleton className="h-3 w-20" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
      {kpiCards.map((kpi) => (
        <Card key={kpi.title} className="hover:shadow-md transition-shadow">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-muted-foreground">{kpi.title}</p>
              <div className={`p-2 rounded-full ${kpi.bgColor}`}>
                <kpi.icon className={`h-4 w-4 ${kpi.color}`} />
              </div>
            </div>
            <p className="text-2xl font-bold">{kpi.value}</p>
            <p className="text-xs text-muted-foreground mt-1">{kpi.description}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};
